import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import StatusChip from "../../components/StatusChip";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Calculator, Send, ThumbsUp, Trash2 } from "lucide-react";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
import ComplianceProofButton from "../../design-system/compliance/ComplianceProofButton";
// Release C-2 (P1) — Open in Microsoft 365 Mail for this record surface.
import OpenInMicrosoftMailButton from "../../design-system/microsoft365/OpenInMicrosoftMailButton";

function money(v) { return `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function Estimates() {
  const [items, setItems] = useState([]);
  const [opps, setOpps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ opportunity_id: "", name: "New Estimate", markup_pct: 15, contingency_pct: 5, tax_pct: 0, line_items: [{ description: "Base scope", qty: 1, uom: "ls", unit_cost_labor: 0, unit_cost_material: 0, unit_cost_equipment: 0, unit_cost_sub: 0, unit_cost_other: 0 }] });
  const [busy, setBusy] = useState(false);
  const [attachId, setAttachId] = useState(null);
  const [attachReload, setAttachReload] = useState(0);

  const load = async () => {
    try {
      const [e, o] = await Promise.all([api.get("/estimates"), api.get("/opportunities")]);
      setItems(e.data); setOpps(o.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { description: "", qty: 1, uom: "ea", unit_cost_labor: 0, unit_cost_material: 0, unit_cost_equipment: 0, unit_cost_sub: 0, unit_cost_other: 0 }] });
  const setLine = (i, k, v) => {
    const li = [...form.line_items]; li[i] = { ...li[i], [k]: v }; setForm({ ...form, line_items: li });
  };
  const removeLine = (i) => setForm({ ...form, line_items: form.line_items.filter((_, ix) => ix !== i) });

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        markup_pct: parseFloat(form.markup_pct || 0),
        contingency_pct: parseFloat(form.contingency_pct || 0),
        tax_pct: parseFloat(form.tax_pct || 0),
        line_items: form.line_items.map((l) => ({
          ...l,
          qty: parseFloat(l.qty || 0),
          unit_cost_labor: parseFloat(l.unit_cost_labor || 0),
          unit_cost_material: parseFloat(l.unit_cost_material || 0),
          unit_cost_equipment: parseFloat(l.unit_cost_equipment || 0),
          unit_cost_sub: parseFloat(l.unit_cost_sub || 0),
          unit_cost_other: parseFloat(l.unit_cost_other || 0),
        })),
      };
      if (!payload.opportunity_id) delete payload.opportunity_id;
      await api.post("/estimates", payload);
      toast.success("Estimate created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitApproval = async (id) => {
    try { await api.post(`/estimates/${id}/submit-for-approval`); toast.success("Submitted for approval"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markApproved = async (id) => {
    try { await api.post(`/estimates/${id}/mark-approved`); toast.success("Estimate approved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell
      title="Estimating · Estimates"
      subtitle={`${items.length} estimate(s) · server-authoritative totals`}
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="estimates-add-btn">
              <Plus className="w-4 h-4 mr-1" /> New estimate
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-erf-cream max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="estimates-add-dialog">
            <DialogHeader><DialogTitle className="font-display">New estimate</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-nav">Opportunity (optional)</Label>
                  <Select value={form.opportunity_id || "__none__"} onValueChange={(v)=>setForm({...form, opportunity_id: v === "__none__" ? "" : v})}>
                    <SelectTrigger className="mt-1" data-testid="est-opportunity"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— none —</SelectItem>
                      {opps.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase font-nav">Name</Label><Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-1" data-testid="est-name" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs uppercase font-nav">Markup %</Label><Input type="number" step="0.01" value={form.markup_pct} onChange={(e)=>setForm({...form, markup_pct: e.target.value})} className="mt-1 font-mono" data-testid="est-markup" /></div>
                <div><Label className="text-xs uppercase font-nav">Contingency %</Label><Input type="number" step="0.01" value={form.contingency_pct} onChange={(e)=>setForm({...form, contingency_pct: e.target.value})} className="mt-1 font-mono" data-testid="est-contingency" /></div>
                <div><Label className="text-xs uppercase font-nav">Tax %</Label><Input type="number" step="0.01" value={form.tax_pct} onChange={(e)=>setForm({...form, tax_pct: e.target.value})} className="mt-1 font-mono" data-testid="est-tax" /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase font-nav">Line items</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="est-add-line"><Plus className="w-3 h-3 mr-1" /> Line</Button>
                </div>
                <div className="space-y-2">
                  {form.line_items.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end border border-erf-borderCream rounded p-2 bg-white" data-testid={`est-line-${i}`}>
                      <div className="col-span-5"><Label className="text-[10px] font-nav uppercase">Description</Label><Input required value={l.description} onChange={(e)=>setLine(i, "description", e.target.value)} className="mt-1 h-8" /></div>
                      <div className="col-span-1"><Label className="text-[10px] font-nav uppercase">Qty</Label><Input type="number" step="0.01" value={l.qty} onChange={(e)=>setLine(i, "qty", e.target.value)} className="mt-1 h-8 font-mono" /></div>
                      <div className="col-span-1"><Label className="text-[10px] font-nav uppercase">UoM</Label><Input value={l.uom} onChange={(e)=>setLine(i, "uom", e.target.value)} className="mt-1 h-8 font-mono" /></div>
                      <div className="col-span-2"><Label className="text-[10px] font-nav uppercase">Labor $</Label><Input type="number" step="0.01" value={l.unit_cost_labor} onChange={(e)=>setLine(i, "unit_cost_labor", e.target.value)} className="mt-1 h-8 font-mono" /></div>
                      <div className="col-span-2"><Label className="text-[10px] font-nav uppercase">Material $</Label><Input type="number" step="0.01" value={l.unit_cost_material} onChange={(e)=>setLine(i, "unit_cost_material", e.target.value)} className="mt-1 h-8 font-mono" /></div>
                      <div className="col-span-1 text-right">
                        {form.line_items.length > 1 && <Button type="button" size="sm" variant="ghost" className="h-8 text-red-700" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="est-submit">{busy ? "Saving…" : "Create estimate"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {items.length === 0 ? (
        <EmptyState icon={Calculator} title="No estimates yet" subtitle="Build your first estimate. Totals are computed server-side." />
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2 mono">v</th>
                <th className="text-right px-3 py-2 mono">Total cost</th>
                <th className="text-right px-3 py-2 mono">Total</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-erf-borderCream/60" data-testid={`est-row-${e.id}`}>
                  <td className="px-3 py-2 font-nav font-semibold">{e.name}</td>
                  <td className="px-3 py-2">
                    <StatusChip
                      status={e.status === "approved" ? "verified" : e.status === "internal_review" ? "pending_approval" : "not_configured"}
                      label={e.status}
                    />
                  </td>
                  <td className="px-3 py-2 mono">{e.version}</td>
                  <td className="px-3 py-2 mono text-right">{money(e.total_cost)}</td>
                  <td className="px-3 py-2 mono text-right font-semibold">{money(e.total)}</td>
                  <td className="px-3 py-2 text-right space-x-1">
                    {/* FINAL-POLISH P2 — Row-level icon-only Open in Outlook (Owner/Admin). */}
                    <span className="inline-block align-middle">
                      <OpenInMicrosoftMailButton
                        surface="estimate_row"
                        recordType="estimate"
                        recordId={e.id}
                        iconOnly
                      />
                    </span>
                    <Link to={`/crm/estimates/${e.id}/edit`} className="inline-flex h-8 items-center px-2 text-sm underline" data-testid={`est-edit-${e.id}`}>Editor</Link>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setAttachId(attachId === e.id ? null : e.id)} data-testid={`est-docs-${e.id}`}>
                      Docs
                    </Button>
                    {e.status === "draft" && (
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => submitApproval(e.id)} data-testid={`est-submit-approval-${e.id}`}>
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    {(e.status === "internal_review" || e.status === "draft") && (
                      <Button size="sm" variant="ghost" className="h-8 text-emerald-700" onClick={() => markApproved(e.id)} data-testid={`est-approve-${e.id}`}>
                        <ThumbsUp className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {attachId && (
        <div className="mt-4 bg-white border border-erf-borderCream rounded-md p-4" data-testid="est-attach-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase font-nav text-erf-dark/60">Take-offs, site plans, and imports for estimate {attachId.slice(0, 8)}</div>
            <div className="flex items-center gap-2">
              <ComplianceProofButton recordType="estimate" recordId={attachId} />
              <OpenInMicrosoftMailButton surface="estimate_detail" recordType="estimate" recordId={attachId} />
              <InformationIntake
                recordType="estimate" recordId={attachId}
                category="import_csv" confidentiality="confidential"
                capabilities={["upload", "multi", "paste", "import_csv", "existing", "scan"]}
                onChanged={() => setAttachReload((k) => k + 1)}
              />
            </div>
          </div>
          <AttachmentsPanel recordType="estimate" recordId={attachId} reload={attachReload} />
        </div>
      )}
    </AppShell>
  );
}
